<!--
@component
AnimatedScore

A small reusable wrapper that renders a numeric (or string) value and
animates a "score bump" whenever it changes: the outgoing value zooms +
fades away while the incoming value pops in.

Powered by Svelte's built-in `scale` transition, fired by `{#key value}`
so each change tears down the old DOM node and mounts a new one. The
old + new nodes are stacked in a single CSS grid cell so there's no
layout shift during the transition.

## Usage

```svelte
<script>
  import AnimatedScore from '$lib/components/common/AnimatedScore/index.svelte';
  let goals = $state(0);
</script>

<AnimatedScore value={goals} />
```

### Tuning the animation

```svelte
<AnimatedScore
  value={goals}
  inDuration={400}
  outDuration={300}
  inStart={1.6}
  outStart={5}
  inOpacity={0}
  outOpacity={0}
/>
```

- `inStart` / `outStart` are scale factors at the *beginning* of the
  in/out transition. (`1` = natural size, `>1` = bigger, `<1` = smaller.)
- `inOpacity` / `outOpacity` are the starting opacities of those
  transitions.
- Mounted resting state is always `scale(1) opacity(1)`.

### Styling

The component is purely structural — it intentionally does NOT set a
font-size, colour or weight, so it inherits from whatever wraps it.
That keeps it a drop-in replacement for any plain `{score}` expression.

Want bolder on change? Pass `bold` (defaults to `false`).
-->

<script lang="ts">
  import { scale } from 'svelte/transition';
  import type { Snippet } from 'svelte';

  type Props = {
    /** The value to render. When it changes, the bump animation fires. */
    value: number | string;
    /** Optional snippet for fully custom rendering of `value`. */
    children?: Snippet<[number | string]>;
    /** Whether the value should be bold while displayed. Default: false. */
    bold?: boolean;
    /** in: transition duration (ms). Default 400. */
    inDuration?: number;
    /** out: transition duration (ms). Default 300. */
    outDuration?: number;
    /** in: starting scale factor. Default 1.6 (pop from bigger → settle). */
    inStart?: number;
    /** out: starting scale factor (what it animates *towards*). Default 1.6. */
    outStart?: number;
    /** in: starting opacity. Default 0. */
    inOpacity?: number;
    /** out: starting opacity. Default 0. */
    outOpacity?: number;
    /** Extra class on the outer stack. */
    class?: string;
  };

  let {
    value,
    children,
    bold = false,
    inDuration = 300,
    outDuration = 350,
    inStart = 1.5,
    outStart = 2,
    inOpacity = 0,
    outOpacity = 0,
    class: cls = '',
  }: Props = $props();
</script>

<span class="animated-score {cls}" class:bold>
  {#key value}
    <span
      class="animated-score__value"
      in:scale={{ duration: inDuration, start: inStart, opacity: inOpacity }}
      out:scale={{
        duration: outDuration,
        start: outStart,
        opacity: outOpacity,
      }}
    >
      {#if children}
        {@render children(value)}
      {:else}
        {value}
      {/if}
    </span>
  {/key}
</span>

<style lang="scss">
  .animated-score {
    /* Stack the old + new value in one grid cell so they overlap during
       the swap and don't cause a layout shift. */
    display: inline-grid;
    grid-template-areas: 'value';
    vertical-align: baseline;

    &.bold > .animated-score__value {
      font-weight: 700;
    }
  }

  .animated-score__value {
    grid-area: value;
    display: inline-block;
    transform-origin: center center;
    will-change: transform, opacity;
  }
</style>
